import axios from 'axios';
import axiosInstance from '../auth/axiosInstance';
import { getApiBaseUrl } from '../auth/apiBaseUrl';
import { getApiErrorMessage } from '../types/api';

export interface SubmitProjectSetupRequestPayload {
  requesterEmail: string;
  companyName: string;
  companyInfo: string;
  companyContactInfo: string;
  industry?: string;
  companySize?: string;
  engagementGoal?: string;
  additionalContext?: string;
  projectName: string;
  departmentName: string;
  businessTypeId?: number;
  projectCreationDate: string;
  smeUserIds?: number[];
}

export interface ProjectSetupRequestItem {
  id: number;
  requesterEmail: string;
  companyName: string;
  companyInfo: string;
  companyContactInfo: string;
  industry: string;
  companySize: string;
  engagementGoal: string;
  additionalContext: string;
  projectName: string;
  departmentName: string;
  businessTypeId?: number;
  businessTypeName: string;
  projectCreationDate: string;
  smeUserIds: number[];
  status: string;
  requestDate: string;
  reviewedBy?: number;
  reviewedByName?: string;
  reviewedAt?: string;
  rejectionReason?: string;
  adminComment?: string;
}

const publicClient = axios.create({
  baseURL: getApiBaseUrl(),
});

function normalizeRequest(raw: Record<string, unknown>): ProjectSetupRequestItem {
  return {
    id: Number(raw.id ?? raw.Id ?? 0),
    requesterEmail: String(raw.requesterEmail ?? raw.RequesterEmail ?? ''),
    companyName: String(raw.companyName ?? raw.CompanyName ?? ''),
    companyInfo: String(raw.companyInfo ?? raw.CompanyInfo ?? ''),
    companyContactInfo: String(raw.companyContactInfo ?? raw.CompanyContactInfo ?? ''),
    industry: String(raw.industry ?? raw.Industry ?? ''),
    companySize: String(raw.companySize ?? raw.CompanySize ?? raw.company_size ?? ''),
    engagementGoal: String(
      raw.engagementGoal ?? raw.EngagementGoal ?? raw.engagement_goal ?? ''
    ),
    additionalContext: String(
      raw.additionalContext ?? raw.AdditionalContext ?? raw.additional_context ?? ''
    ),
    projectName: String(raw.projectName ?? raw.ProjectName ?? ''),
    departmentName: String(raw.departmentName ?? raw.DepartmentName ?? ''),
    businessTypeId: Number(raw.businessTypeId ?? raw.BusinessTypeId ?? 0),
    businessTypeName: String(raw.businessTypeName ?? raw.BusinessTypeName ?? ''),
    projectCreationDate: String(raw.projectCreationDate ?? raw.ProjectCreationDate ?? ''),
    smeUserIds: Array.isArray(raw.smeUserIds ?? raw.SmeUserIds)
      ? (raw.smeUserIds ?? raw.SmeUserIds).map((id) => Number(id))
      : [],
    status: String(raw.status ?? raw.Status ?? ''),
    requestDate: String(raw.requestDate ?? raw.RequestDate ?? ''),
    reviewedBy: raw.reviewedBy != null ? Number(raw.reviewedBy) : undefined,
    reviewedByName: raw.reviewedByName ? String(raw.reviewedByName) : undefined,
    reviewedAt: raw.reviewedAt ? String(raw.reviewedAt) : undefined,
    rejectionReason: raw.rejectionReason ? String(raw.rejectionReason) : undefined,
    adminComment: raw.adminComment ? String(raw.adminComment) : undefined,
  };
}

export async function getPublicBusinessTypes() {
  const { data } = await publicClient.get('/ProjectSetupRequest/business-types', {
    params: { pageNumber: 1, pageSize: 100 },
  });

  const items = data?.data?.items ?? data?.data?.Items ?? [];
  return Array.isArray(items)
    ? items.filter((item: { isActive?: boolean; IsActive?: boolean }) =>
        item.isActive === true || item.IsActive === true || item.isActive === undefined
      )
    : [];
}

/** Public list of active department names for the request form dropdown. */
export async function getPublicDepartmentNames(): Promise<string[]> {
  const { data } = await publicClient.get('/ProjectSetupRequest/departments');
  const items = data?.data ?? data?.Data ?? [];
  return Array.isArray(items)
    ? items.map((name) => String(name ?? '').trim()).filter(Boolean)
    : [];
}

export async function submitProjectSetupRequest(payload: SubmitProjectSetupRequestPayload) {
  const { data } = await publicClient.post('/ProjectSetupRequest/submit', payload);

  if (data?.succeeded === false || data?.code === 400) {
    throw new Error(data?.message || 'Failed to submit request');
  }

  return data?.data ?? data;
}

export async function getProjectSetupRequests(
  searchTerm = '',
  status = 'All',
  pageNumber = 1,
  pageSize = 10,
  sort = ''
) {
  const { data } = await axiosInstance.post('/ProjectSetupRequest/list', {
    searchTerm,
    status,
    pageNumber,
    pageSize,
    ...(sort && { sort }),
  });

  const page = data?.data;
  const rows = Array.isArray(page?.data) ? page.data.map(normalizeRequest) : [];

  return {
    data: rows,
    totalRecords: page?.totalRecords ?? 0,
    totalPages: page?.totalPages ?? 1,
    pageNumber: page?.pageNumber ?? pageNumber,
    pageSize: page?.pageSize ?? pageSize,
    hasNextPage: page?.hasNextPage ?? false,
    hasPreviousPage: page?.hasPreviousPage ?? false,
  };
}

export async function approveRejectProjectSetupRequest(payload: {
  id: number;
  action: 'Approve' | 'Reject';
  rejectionReason?: string;
  adminComment?: string;
}) {
  try {
    const { data } = await axiosInstance.post('/ProjectSetupRequest/approve-reject', payload);
    if (data?.success === false) {
      throw new Error(data?.message || 'Failed to process request');
    }
    return data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
}
